import { FC, useEffect, useState } from 'react'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import { DetailField, DetailFields, DetailPanel, Dispositivos } from '../../../../../../components'
import { AnotacaoType, showError } from '../../../../../../../_metronic/helpers'
import { getTiposSituacoesDiagnostico } from '../../../../../../../_metronic/helpers/crud-helper/_request'

type IProps = {
  isLoading?: boolean
  data?: AtendimentoView
}

const formatarAnotacao = (texto: string) => texto.replace(/\n+/g, ' | ').replace(/\s+/g, ' ').trim()

/** Lista "A, B e C". */
const listaPorExtenso = (itens: string[]) => itens.join(', ').replace(/,([^,]*)$/, ' e$1')

export const AtendimentoExpandedRow: FC<IProps> = ({ isLoading, data }) => {
  const temCids = !!data?.cids && data.cids.length > 0

  // Sem CID, a situação diagnóstica vem do enum: busca a descrição legível.
  const [situacaoDescricao, setSituacaoDescricao] = useState<string>()
  const [loadingSituacao, setLoadingSituacao] = useState(false)

  useEffect(() => {
    if (temCids || !data?.situacaoDiagnostico) {
      setSituacaoDescricao(undefined)
      return
    }
    let cancelled = false
    setLoadingSituacao(true)
    getTiposSituacoesDiagnostico()
      .then((tipos) => {
        if (cancelled) return
        const tipo = tipos.find((t) => t.name === data.situacaoDiagnostico)
        setSituacaoDescricao(tipo?.descricao)
      })
      .catch((error) => {
        if (!cancelled) showError(error)
      })
      .finally(() => {
        if (!cancelled) setLoadingSituacao(false)
      })
    return () => {
      cancelled = true
    }
  }, [temCids, data?.situacaoDiagnostico])

  if (!isLoading && !data) return null

  const situacaoDiagnostico = temCids
    ? data!.cids!.map((c) => `${c.cid.codigo} - ${c.cid.descricao}`).join(', ')
    : loadingSituacao
    ? 'Carregando…'
    : situacaoDescricao

  const anotacoes =
    data?.anotacoes?.filter(
      (a) =>
        a.tipo === AnotacaoType.Pendencia ||
        a.tipo === AnotacaoType.Observacao ||
        a.tipo === AnotacaoType.Replica
    ) ?? []

  const procedimentos = (data?.procedimentos ?? []).map((p) => p.nome).filter(Boolean) as string[]

  return (
    <DetailPanel isLoading={isLoading} loadingLabel='Carregando detalhes do atendimento…'>
      {data && (
        <DetailFields>
          <DetailField
            label='Sinais'
            value={
              data.dispositivos ? <Dispositivos dispositivos={data.dispositivos} /> : undefined
            }
            emptyLabel='Sem sinais'
            wide
          />
          <DetailField label='Retorno' value={data.retorno ? 'Sim' : 'Não'} />
          <DetailField label='Situação diagnóstica' value={situacaoDiagnostico} wide />
          <DetailField
            label='Anotações'
            value={
              anotacoes.length > 0 ? (
                <ul className='list-unstyled m-0 d-flex flex-column gap-1'>
                  {anotacoes.map((anotacao, index) => (
                    <li key={index} className='fs-7'>
                      <span className='text-gray-700'>{anotacao.dataFormatada}</span> ·{' '}
                      <span className='fw-semibold'>{anotacao.usuario?.nome}</span> ·{' '}
                      {anotacao.tipo === AnotacaoType.Replica && (
                        <span className='badge badge-light-info text-gray-900 me-1'>
                          Réplica do solicitante
                        </span>
                      )}
                      <span className='fw-normal'>
                        {formatarAnotacao(anotacao.anotacao as string)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : undefined
            }
            emptyLabel='Sem anotações'
            wide
          />
          <DetailField
            label='Procedimentos'
            value={procedimentos.length > 0 ? listaPorExtenso(procedimentos) : undefined}
            emptyLabel='Sem procedimentos'
            wide
          />
        </DetailFields>
      )}
    </DetailPanel>
  )
}
